import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api'
import { useAuth } from '../auth'
import PortalTab from './PortalTab'
import SubscriptionTab from './SubscriptionTab'
import AccessEditor from './AccessEditor'
import CopyField, { CopyAllButton, buildAccessText } from './CopyField'
import type { Access, AccessKind, Client, Contact } from '../types'
import { ACCESS_KIND_LABEL } from '../types'
import { useEscClose } from '../lib/useEscClose'

interface Props {
  clientId: number
  clientName: string
  /** С какой вкладки открыть: из журнала жмут «Доступы» или «Контакты». */
  tab?: 'contacts' | 'accesses' | 'card' | 'portal' | 'tariff'
  onClose: () => void
}

export default function ClientCard({ clientId, clientName, tab = 'accesses', onClose }: Props) {
  const [active, setActive] = useState(tab)

  /* Имя берём из справочника, а не из того, с чем карточку открыли: после
     переименования заголовок должен смениться сразу, не дожидаясь возврата
     в журнал. */
  const clients = useQuery({
    queryKey: ['clients', 'all'],
    queryFn: () => api.clients({ include_inactive: true }),
  })
  const client = clients.data?.find((c) => c.id === clientId) ?? null

  useEscClose(onClose)

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal wide">
        <h2>{client?.name ?? clientName}</h2>

        <div className="modal-body">
        <div className="tabs">
          <button
            className={active === 'card' ? 'active' : ''}
            onClick={() => setActive('card')}
          >
            Клиент
          </button>
          <button
            className={active === 'accesses' ? 'active' : ''}
            onClick={() => setActive('accesses')}
          >
            Доступы
          </button>
          <button
            className={active === 'contacts' ? 'active' : ''}
            onClick={() => setActive('contacts')}
          >
            Контакты
          </button>
          <button
            className={active === 'tariff' ? 'active' : ''}
            onClick={() => setActive('tariff')}
          >
            Тариф
          </button>
          <button
            className={active === 'portal' ? 'active' : ''}
            onClick={() => setActive('portal')}
          >
            Кабинет
          </button>
        </div>

        {active === 'card' ? (
          <ClientForm clientId={clientId} client={client} loading={clients.isLoading} />
        ) : active === 'tariff' ? (
          <SubscriptionTab clientId={clientId} client={client} />
        ) : active === 'portal' ? (
          <PortalTab clientId={clientId} client={client} />
        ) : active === 'accesses' ? (
          <Accesses clientId={clientId} />
        ) : (
          <Contacts clientId={clientId} />
        )}
        </div>

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Сведения о клиенте с возможностью правки.
 *
 * Форма с явной кнопкой «Сохранить», а не правка по месту, как в справочнике:
 * здесь полей много и они связаны — переименование, ИНН, контакты. Сохранять
 * каждое нажатие клавиши отдельным запросом значило бы плодить промежуточные
 * состояния вроде клиента с половиной названия.
 */
function ClientForm({
  clientId,
  client,
  loading,
}: {
  clientId: number
  client: Client | null
  loading: boolean
}) {
  const qc = useQueryClient()
  const { can } = useAuth()
  const mayEdit = can('clients.edit')

  const [form, setForm] = useState<Record<string, string>>({})
  const [flags, setFlags] = useState({ is_active: true, has_projects: false })
  // Особые возможности клиента. Набором, а не парой галочек в коде: список
  // приходит с сервера, и новая возможность появляется здесь сама.
  const [feats, setFeats] = useState<string[]>([])
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [ready, setReady] = useState(false)

  const sources = useQuery({ queryKey: ['sources'], queryFn: () => api.sources() })
  const catalog = useQuery({ queryKey: ['client-features'], queryFn: () => api.clientFeatures() })
  const others = useQuery({
    queryKey: ['clients', 'all'],
    queryFn: () => api.clients({ include_inactive: true }),
  })

  // Заполняем форму, когда справочник доехал. Один раз: иначе фоновое
  // обновление списка затирало бы то, что человек уже набрал.
  if (client && !ready) {
    setForm({
      name: client.name,
      inn: client.inn ?? '',
      contact_person: client.contact_person ?? '',
      phone: client.phone ?? '',
      email: client.email ?? '',
      note: client.note ?? '',
      source_id: client.source_id ? String(client.source_id) : '',
      parent_id: client.parent_id ? String(client.parent_id) : '',
    })
    setFlags({ is_active: client.is_active, has_projects: client.has_projects })
    setFeats(client.features ?? [])
    setReady(true)
  }

  const save = useMutation({
    mutationFn: () =>
      api.updateClient(clientId, {
        name: form.name.trim(),
        inn: form.inn.trim() || null,
        contact_person: form.contact_person.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        note: form.note.trim() || null,
        source_id: form.source_id ? Number(form.source_id) : null,
        parent_id: form.parent_id ? Number(form.parent_id) : null,
        is_active: flags.is_active,
        has_projects: flags.has_projects,
        features: feats,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] })
      qc.invalidateQueries({ queryKey: ['tasks'] })
      setSaved(true)
      window.setTimeout(() => setSaved(false), 2000)
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось сохранить'),
  })

  if (loading) return <p className="empty">Загрузка…</p>
  if (!client) return <p className="empty">Клиент не найден</p>

  const set = (key: string) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value })

  return (
    <>
      {error && <div className="error">{error}</div>}
      {!mayEdit && (
        <div className="warn">Сведения только для чтения — нет права на редактирование клиентов.</div>
      )}

      <div className="grid-2">
        <div className="field full field-accent">
          <label>Название</label>
          <input value={form.name ?? ''} onChange={set('name')} disabled={!mayEdit} required />
        </div>

        <div className="field">
          <label>ИНН</label>
          <input value={form.inn ?? ''} onChange={set('inn')} disabled={!mayEdit} />
        </div>

        <div className="field">
          <label>Источник по умолчанию</label>
          <select value={form.source_id ?? ''} onChange={set('source_id')} disabled={!mayEdit}>
            <option value="">—</option>
            {sources.data?.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
          <span className="hint-inline">Подставляется в новые задачи этого клиента</span>
        </div>

        <div className="field">
          <label>Контактное лицо</label>
          <input
            value={form.contact_person ?? ''}
            onChange={set('contact_person')}
            disabled={!mayEdit}
          />
        </div>

        <div className="field">
          <label>Телефон</label>
          <input value={form.phone ?? ''} onChange={set('phone')} disabled={!mayEdit} />
        </div>

        <div className="field">
          <label>Почта</label>
          <input value={form.email ?? ''} onChange={set('email')} disabled={!mayEdit} />
        </div>

        <div
          className="field"
          title="Группа компаний: клиенты остаются раздельными, связь только для отчётности"
        >
          <label>Головная организация</label>
          <select value={form.parent_id ?? ''} onChange={set('parent_id')} disabled={!mayEdit}>
            <option value="">— самостоятельный —</option>
            {others.data
              ?.filter((x) => x.id !== clientId)
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
          </select>
        </div>

        <div className="field full">
          <label>Заметка</label>
          <textarea rows={2} value={form.note ?? ''} onChange={set('note')} disabled={!mayEdit} />
        </div>

        <div className="field full">
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={flags.has_projects}
              onChange={(e) => setFlags({ ...flags, has_projects: e.target.checked })}
              disabled={!mayEdit}
            />
            <span>Ведутся проекты — в задачах появится поле «Проект»</span>
          </label>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={flags.is_active}
              onChange={(e) => setFlags({ ...flags, is_active: e.target.checked })}
              disabled={!mayEdit}
            />
            <span>Активен — снятый клиент не предлагается в новых задачах</span>
          </label>
        </div>

        {(catalog.data?.length ?? 0) > 0 && (
          <div className="field full">
            <label>Особые возможности</label>
            <span className="hint-inline">
              Разделы, которые ведутся не для всех. Снимете галочку — раздел
              пропадёт и у нас, и в кабинете клиента, а записи останутся на месте.
            </span>
            {catalog.data?.map((f) => (
              <label key={f.key} className="checkbox-row" style={{ alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={feats.includes(f.key)}
                  onChange={(e) =>
                    setFeats(
                      e.target.checked
                        ? [...feats, f.key]
                        : feats.filter((k) => k !== f.key),
                    )
                  }
                  disabled={!mayEdit}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <b>{f.title}</b>
                  <span className="hint-inline" style={{ display: 'block' }}>
                    {f.hint}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {mayEdit && (
        <div className="estimate-tools">
          <span className="hint-inline">
            {saved ? '✓ Сохранено' : 'Изменения применятся после сохранения'}
          </span>
          <div className="estimate-tools-right">
            <button
              className="btn primary"
              disabled={save.isPending || !(form.name ?? '').trim()}
              onClick={() => {
                setError('')
                save.mutate()
              }}
            >
              {save.isPending ? 'Сохраняем…' : 'Сохранить'}
            </button>
          </div>
        </div>
      )}
    </>
  )
}

/** Вся запись доступа одним текстом — для кнопки «Копировать». */
function accessToText(a: Access): string {
  const head: [string, string | null | undefined][] = [
    ['Тип', ACCESS_KIND_LABEL[a.kind]],
    ['Название', a.title],
    ['Продукт', a.product_name],
    ['Сервис', a.service_name],
    ['Логин', a.login],
    ['Пароль', a.password],
    ['Адрес сервера', a.server_address],
    ['Телефон восстановления', a.recovery_phone],
    ['Почта восстановления', a.recovery_email],
  ]
  const parts = [buildAccessText(head)]

  const block = (title: string, rows: typeof a.rows, fields: [string, keyof (typeof a.rows)[0]][]) => {
    if (rows.length === 0) return
    const body = rows
      .map((r) => buildAccessText(fields.map(([label, key]) => [label, r[key] as string | null])))
      .join('\n---\n')
    parts.push(`\n[${title}]\n${body}`)
  }

  block('Базы', a.rows.filter((r) => r.row_type === 'base_link'), [
    ['Ссылка', 'url'],
    ['Продукт', 'product_name'],
  ])
  block('Компьютеры', a.rows.filter((r) => r.row_type === 'computer'), [
    ['Компьютер', 'computer'],
    ['Пользователь', 'user_name'],
  ])
  block('Пользователи базы', a.rows.filter((r) => r.row_type === 'credential'), [
    ['Пользователь', 'user_name'],
    ['Логин', 'login'],
    ['Пароль', 'password'],
  ])
  block('Входы', a.rows.filter((r) => r.row_type === 'portal_entry'), [
    ['Ссылка', 'url'],
    ['Логин', 'login'],
    ['Пароль', 'password'],
  ])

  if (a.note) parts.push(`\nПримечание: ${a.note}`)
  return parts.join('\n')
}


/* ---------------- доступы ---------------- */

function Accesses({ clientId }: { clientId: number }) {
  const qc = useQueryClient()
  const [openId, setOpenId] = useState<number | null>(null)
  const [editing, setEditing] = useState<{ access: Access | null; kind: AccessKind } | null>(null)
  const [error, setError] = useState('')

  const list = useQuery({
    queryKey: ['accesses', clientId],
    queryFn: () => api.accesses(clientId),
  })

  // Пароли приходят только по запросу конкретной записи — поэтому отдельный запрос
  const opened = useQuery({
    queryKey: ['access', openId],
    queryFn: () => api.access(openId!),
    enabled: openId !== null,
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteAccess(id),
    onSuccess: () => {
      setOpenId(null)
      qc.invalidateQueries({ queryKey: ['accesses', clientId] })
      qc.invalidateQueries({ queryKey: ['clients'] })
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось удалить'),
  })

  return (
    <>
      {error && (
        <div className="error" onClick={() => setError('')}>
          {error}
        </div>
      )}

      <div className="toolbar">
        {(['cloud', 'database', 'portal'] as AccessKind[]).map((k) => (
          <button key={k} className="btn" onClick={() => setEditing({ access: null, kind: k })}>
            + {ACCESS_KIND_LABEL[k]}
          </button>
        ))}
      </div>

      {list.isLoading && <div className="empty">Загрузка…</div>}
      {list.data?.length === 0 && <div className="empty">Доступов пока нет</div>}

      {list.data?.map((a) => {
        const isOpen = openId === a.id
        const full = isOpen ? opened.data : null
        return (
          <div key={a.id} className="access-card">
            <div className="access-head">
              <div>
                <span className={`badge kind-${a.kind}`}>{ACCESS_KIND_LABEL[a.kind]}</span>{' '}
                <b>{a.title}</b>
                {a.product_name && (
                  <span style={{ color: 'var(--muted)' }}> · {a.product_name}</span>
                )}
                {a.service_name && (
                  <span style={{ color: 'var(--muted)' }}> · {a.service_name}</span>
                )}
              </div>
              <div>
                <button className="btn small" onClick={() => setOpenId(isOpen ? null : a.id)}>
                  {isOpen ? 'Свернуть' : 'Показать'}
                </button>{' '}
                <button
                  className="btn small"
                  onClick={() => setEditing({ access: full ?? a, kind: a.kind })}
                  disabled={isOpen && opened.isLoading}
                  title={isOpen ? undefined : 'Сначала раскройте запись, чтобы править пароли'}
                >
                  Изменить
                </button>{' '}
                <button
                  className="btn small danger"
                  onClick={() => {
                    if (confirm(`Удалить доступ «${a.title}»?`)) remove.mutate(a.id)
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            {isOpen && (
              <div className="access-body">
                {opened.isLoading && <div className="empty">Загрузка…</div>}
                {full && (
                  <>
                    <CopyField label="Логин" value={full.login} />
                    <CopyField label="Пароль" value={full.password} secret />
                    <CopyField label="Адрес сервера" value={full.server_address} />
                    <CopyField label="Телефон восстановления" value={full.recovery_phone} />
                    <CopyField label="Почта восстановления" value={full.recovery_email} />

                    {full.rows.filter((r) => r.row_type === 'base_link').length > 0 && (
                      <div className="access-group">
                        <div className="access-group-title">Базы</div>
                        {full.rows
                          .filter((r) => r.row_type === 'base_link')
                          .map((r, i) => (
                            <div key={r.id ?? i} className="access-row-view">
                              <CopyField
                                label={r.product_name ? `Ссылка · ${r.product_name}` : 'Ссылка'}
                                value={r.url}
                              />
                            </div>
                          ))}
                      </div>
                    )}

                    {full.rows.filter((r) => r.row_type === 'computer').length > 0 && (
                      <div className="access-group">
                        <div className="access-group-title">Компьютеры</div>
                        {full.rows
                          .filter((r) => r.row_type === 'computer')
                          .map((r, i) => (
                            <div key={r.id ?? i} className="access-row-view">
                              <CopyField label="Компьютер" value={r.computer} />
                              <CopyField label="Пользователь" value={r.user_name} />
                            </div>
                          ))}
                      </div>
                    )}

                    {full.rows.filter((r) => r.row_type === 'credential').length > 0 && (
                      <div className="access-group">
                        <div className="access-group-title">Пользователи базы</div>
                        {full.rows
                          .filter((r) => r.row_type === 'credential')
                          .map((r, i) => (
                            <div key={r.id ?? i} className="access-row-view">
                              <CopyField label="Пользователь" value={r.user_name} />
                              <CopyField label="Логин" value={r.login} />
                              <CopyField label="Пароль" value={r.password} secret />
                            </div>
                          ))}
                      </div>
                    )}

                    {full.rows.filter((r) => r.row_type === 'portal_entry').length > 0 && (
                      <div className="access-group">
                        <div className="access-group-title">Входы</div>
                        {full.rows
                          .filter((r) => r.row_type === 'portal_entry')
                          .map((r, i) => (
                            <div key={r.id ?? i} className="access-row-view">
                              <CopyField label="Ссылка" value={r.url} />
                              <CopyField label="Логин" value={r.login} />
                              <CopyField label="Пароль" value={r.password} secret />
                            </div>
                          ))}
                      </div>
                    )}

                    {full.files.length > 0 && (
                      <div className="access-group">
                        <div className="access-group-title">Файлы</div>
                        {full.files.map((f) => (
                          <div key={f.id} className="copy-field">
                            <span className="copy-label">Файл</span>
                            <span className="copy-value">{f.original_name}</span>
                            <button
                              className="btn small"
                              onClick={() => api.downloadAccessFile(f.id, f.original_name)}
                            >
                              Скачать
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {full.note && <p style={{ color: 'var(--muted)' }}>{full.note}</p>}

                    <div className="access-copy-all">
                      <CopyAllButton text={accessToText(full)} label="Копировать" />
                      <span className="hint-inline">
                        Копирует всю запись. Одно значение — щёлкните по нему и Ctrl+C
                        или правой кнопкой мыши.
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )
      })}

      {editing && (
        <AccessEditor
          clientId={clientId}
          access={editing.access}
          kind={editing.kind}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  )
}

/* ---------------- контакты ---------------- */

const BLANK = { full_name: '', role: '', phone: '', email: '', telegram: '' }

function Contacts({ clientId }: { clientId: number }) {
  const qc = useQueryClient()
  const [draft, setDraft] = useState({ ...BLANK })
  const [editing, setEditing] = useState<Contact | null>(null)
  const [error, setError] = useState('')

  const list = useQuery({ queryKey: ['contacts', clientId], queryFn: () => api.contacts(clientId) })
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['contacts', clientId] })
    qc.invalidateQueries({ queryKey: ['clients'] })
  }

  const create = useMutation({
    mutationFn: () => api.createContact(clientId, draft),
    onSuccess: () => {
      setDraft({ ...BLANK })
      refresh()
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось добавить контакт'),
  })

  const update = useMutation({
    mutationFn: (c: Contact) =>
      api.updateContact(c.id, {
        full_name: c.full_name,
        role: c.role,
        phone: c.phone,
        email: c.email,
        telegram: c.telegram,
      }),
    onSuccess: () => {
      setEditing(null)
      refresh()
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось сохранить контакт'),
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteContact(id),
    onSuccess: refresh,
  })

  return (
    <>
      {error && (
        <div className="error" onClick={() => setError('')}>
          {error}
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>ФИО</th>
              <th>Должность</th>
              <th>Телефон</th>
              <th>Почта</th>
              <th>Телеграм</th>
              <th style={{ width: 150 }} />
            </tr>
          </thead>
          <tbody>
            {list.data?.length === 0 && (
              <tr>
                <td colSpan={6} className="empty">
                  Контактов пока нет
                </td>
              </tr>
            )}
            {list.data?.map((c) =>
              editing?.id === c.id ? (
                <tr key={c.id}>
                  {(['full_name', 'role', 'phone', 'email', 'telegram'] as const).map((f) => (
                    <td key={f}>
                      <input
                        value={editing[f] ?? ''}
                        onChange={(e) => setEditing({ ...editing, [f]: e.target.value })}
                        style={{ width: '100%' }}
                      />
                    </td>
                  ))}
                  <td>
                    <button className="btn small primary" onClick={() => update.mutate(editing)}>
                      Сохранить
                    </button>{' '}
                    <button className="btn small" onClick={() => setEditing(null)}>
                      Отмена
                    </button>
                  </td>
                </tr>
              ) : (
                <tr key={c.id}>
                  <td>{c.full_name}</td>
                  <td>{c.role ?? ''}</td>
                  <td className="mono">{c.phone ?? ''}</td>
                  <td className="mono">{c.email ?? ''}</td>
                  <td className="mono">{c.telegram ?? ''}</td>
                  <td>
                    <button className="btn small" onClick={() => setEditing(c)}>
                      Изменить
                    </button>{' '}
                    <button className="btn small danger" onClick={() => remove.mutate(c.id)}>
                      ✕
                    </button>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>

      <div className="toolbar" style={{ marginTop: 12 }}>
        {(
          [
            ['full_name', 'ФИО'],
            ['role', 'Должность'],
            ['phone', 'Телефон'],
            ['email', 'Почта'],
            ['telegram', 'Телеграм'],
          ] as const
        ).map(([key, label]) => (
          <div className="field" key={key} style={{ flex: 1, minWidth: 130 }}>
            <label>{label}</label>
            <input
              value={draft[key]}
              onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
            />
          </div>
        ))}
        <button
          className="btn primary"
          disabled={!draft.full_name.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          + Контакт
        </button>
      </div>
    </>
  )
}
